import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import baseURL from '../utils/baseURL';
import useAxiosAuth from '../hooks/useAxiosAuth';

// Helper function to extract serializable error information
const getSerializableError = (error) => {
    if (!error) return 'An unknown error occurred';
    if (typeof error === 'string') return error;
    if (error.response) {
        return error.response.data?.message || error.message || `Request failed with status code ${error.response.status}`;
    }
    return error.message || 'An error occurred';
};

// ✅ Get Doctors by Date Range
export const getDoctorsByDate = createAsyncThunk(
    'reports/getDoctorsByDate',
    async ({ from, to, page = 1, limit = 5, gender = '', countryId = '' }, { rejectWithValue }) => {
        const axiosInstance = useAxiosAuth();
        try {
            const params = { from, to, page, limit };
            if (gender) params.gender = gender;
            if (countryId) params.countryId = countryId;

            const response = await axiosInstance.get(`${baseURL}/doctors/by-date`, {
                params
            });
            return response.data;
        } catch (error) {
            return rejectWithValue({ error: getSerializableError(error) });
        }
    }
);

// ✅ Download Doctors Excel by Date Range
export const downloadDoctorsExcel = createAsyncThunk(
    'reports/downloadDoctorsExcel',
    async ({ from, to, gender = '', countryId = '' }, { rejectWithValue }) => {
        const axiosInstance = useAxiosAuth();
        try {
            const params = { from, to };
            if (gender) params.gender = gender;
            if (countryId) params.countryId = countryId;

            const response = await axiosInstance.get(`${baseURL}/doctors/download`, {
                params,
                responseType: 'blob'
            });
            return response.data;
        } catch (error) {
            return rejectWithValue({ error: getSerializableError(error) });
        }
    }
);

// ✅ Get Patients by Date Range
export const getPatientsByDate = createAsyncThunk(
    'reports/getPatientsByDate',
    async ({ from, to, page = 1, limit = 5, doctorId, gender = '', countryId = '', timeZone = '' }, { rejectWithValue }) => {
        const axiosInstance = useAxiosAuth();
        try {
            const params = { from, to, page, limit };
            params.timeZone = timeZone || Intl.DateTimeFormat().resolvedOptions().timeZone;
            if (doctorId) {
                params.doctorId = doctorId;
            }
            if (gender) {
                params.gender = gender;
            }
            if (countryId) {
                params.countryId = countryId;
            }
            const response = await axiosInstance.get(`${baseURL}/patients/by-date`, {
                params
            });
            return response.data;
        } catch (error) {
            return rejectWithValue({ error: getSerializableError(error) });
        }
    }
);

// ✅ Download Patients Excel by Date Range
export const downloadPatientsExcel = createAsyncThunk(
    'reports/downloadPatientsExcel',
    async ({ from, to, doctorId, gender = '', countryId = '', timeZone = '' }, { rejectWithValue }) => {
        const axiosInstance = useAxiosAuth();
        try {
            const params = { from, to };
            params.timeZone = timeZone || Intl.DateTimeFormat().resolvedOptions().timeZone;
            if (doctorId) {
                params.doctorId = doctorId;
            }
            if (gender) {
                params.gender = gender;
            }
            if (countryId) {
                params.countryId = countryId;
            }
            const response = await axiosInstance.get(`${baseURL}/patients/download`, {
                params,
                responseType: 'blob'
            });
            return response.data;
        } catch (error) {
            return rejectWithValue({ error: getSerializableError(error) });
        }
    }
);

// ✅ Get Doctors with Pending Profile
export const getPendingProfileDoctors = createAsyncThunk(
    'reports/getPendingProfileDoctors',
    async ({ page = 1, limit = 5, gender = '', countryId = '', pending_data = '' }, { rejectWithValue }) => {
        const axiosInstance = useAxiosAuth();
        try {
            const params = { page, limit };
            if (gender) params.gender = gender;
            if (countryId) params.countryId = countryId;
            if (pending_data) params.pending_data = pending_data;

            const response = await axiosInstance.get(`${baseURL}/doctors/profile-pending`, {
                params
            });
            return response.data;
        } catch (error) {
            return rejectWithValue({ error: getSerializableError(error) });
        }
    }
);

// ✅ Download Pending Profile Doctors Excel
export const downloadPendingProfileExcel = createAsyncThunk(
    'reports/downloadPendingProfileExcel',
    async ({ gender = '', countryId = '', pending_data = '' } = {}, { rejectWithValue }) => {
        const axiosInstance = useAxiosAuth();
        try {
            const params = {};
            if (gender) params.gender = gender;
            if (countryId) params.countryId = countryId;
            if (pending_data) params.pending_data = pending_data;

            const response = await axiosInstance.get(`${baseURL}/doctors/profile-pending/download`, {
                params,
                responseType: 'blob'
            });
            return response.data;
        } catch (error) {
            return rejectWithValue({ error: getSerializableError(error) });
        }
    }
);

// ✅ Get Doctors with Zero Patients
export const getZeroPatientsDoctors = createAsyncThunk(
    'reports/getZeroPatientsDoctors',
    async ({ page = 1, limit = 5, gender = '', countryId = '' }, { rejectWithValue }) => {
        const axiosInstance = useAxiosAuth();
        try {
            const params = { page, limit };
            if (gender) params.gender = gender;
            if (countryId) params.countryId = countryId;
            
            const response = await axiosInstance.get(`${baseURL}/doctors/zero-patients`, {
                params
            });
            return response.data;
        } catch (error) {
            return rejectWithValue({ error: getSerializableError(error) });
        }
    }
);

// ✅ Download Zero Patients Doctors Excel
export const downloadZeroPatientsExcel = createAsyncThunk(
    'reports/downloadZeroPatientsExcel',
    async ({ gender = '', countryId = '' } = {}, { rejectWithValue }) => {
        const axiosInstance = useAxiosAuth();
        try {
            const params = {};
            if (gender) params.gender = gender;
            if (countryId) params.countryId = countryId;
            
            const response = await axiosInstance.get(`${baseURL}/doctors/zero-patients/download`, {
                params,
                responseType: 'blob'
            });
            return response.data;
        } catch (error) {
            return rejectWithValue({ error: getSerializableError(error) });
        }
    }
);

// ✅ Get Doctors with Zero Appointments
export const getZeroAppointmentsDoctors = createAsyncThunk(
    'reports/getZeroAppointmentsDoctors',
    async ({ page = 1, limit = 5, gender = '', countryId = '' }, { rejectWithValue }) => {
        const axiosInstance = useAxiosAuth();
        try {
            const params = { page, limit };
            if (gender) params.gender = gender;
            if (countryId) params.countryId = countryId;
            
            const response = await axiosInstance.get(`${baseURL}/doctors/zero-appointments`, {
                params
            });
            return response.data;
        } catch (error) {
            return rejectWithValue({ error: getSerializableError(error) });
        }
    }
);

// ✅ Download Zero Appointments Doctors Excel
export const downloadZeroAppointmentsExcel = createAsyncThunk(
    'reports/downloadZeroAppointmentsExcel',
    async ({ gender = '', countryId = '' } = {}, { rejectWithValue }) => {
        const axiosInstance = useAxiosAuth();
        try {
            const params = {};
            if (gender) params.gender = gender;
            if (countryId) params.countryId = countryId;
            
            const response = await axiosInstance.get(`${baseURL}/doctors/zero-appointments/download`, {
                params,
                responseType: 'blob'
            });
            return response.data;
        } catch (error) {
            return rejectWithValue({ error: getSerializableError(error) });
        }
    }
);

// ✅ Get Doctors with Less Than Ten Appointments
export const getLessThanTenAppointmentsDoctors = createAsyncThunk(
    'reports/getLessThanTenAppointmentsDoctors',
    async ({ page = 1, limit = 5, gender = '', countryId = '' }, { rejectWithValue }) => {
        const axiosInstance = useAxiosAuth();
        try {
            const params = { page, limit };
            if (gender) params.gender = gender;
            if (countryId) params.countryId = countryId;
            
            const response = await axiosInstance.get(`${baseURL}/doctors/less-than-ten-appointments`, {
                params
            });
            return response.data;
        } catch (error) {
            return rejectWithValue({ error: getSerializableError(error) });
        }
    }
);

// ✅ Download Less Than Ten Appointments Doctors Excel
export const downloadLessThanTenAppointmentsExcel = createAsyncThunk(
    'reports/downloadLessThanTenAppointmentsExcel',
    async ({ gender = '', countryId = '' } = {}, { rejectWithValue }) => {
        const axiosInstance = useAxiosAuth();
        try {
            const params = {};
            if (gender) params.gender = gender;
            if (countryId) params.countryId = countryId;
            
            const response = await axiosInstance.get(`${baseURL}/doctors/less-than-ten-appointments/download`, {
                params,
                responseType: 'blob'
            });
            return response.data;
        } catch (error) {
            return rejectWithValue({ error: getSerializableError(error) });
        }
    }
);

// ✅ Get Doctors with Zero Login
export const getZeroLoginDoctors = createAsyncThunk(
    'reports/getZeroLoginDoctors',
    async ({ page = 1, limit = 5, gender = '', countryId = '' }, { rejectWithValue }) => {
        const axiosInstance = useAxiosAuth();
        try {
            const params = { page, limit };
            if (gender) params.gender = gender;
            if (countryId) params.countryId = countryId;
            
            const response = await axiosInstance.get(`${baseURL}/doctors/zero-login`, {
                params
            });
            return response.data;
        } catch (error) {
            return rejectWithValue({ error: getSerializableError(error) });
        }
    }
);

// ✅ Download Zero Login Doctors Excel
export const downloadZeroLoginExcel = createAsyncThunk(
    'reports/downloadZeroLoginExcel',
    async ({ gender = '', countryId = '' } = {}, { rejectWithValue }) => {
        const axiosInstance = useAxiosAuth();
        try {
            const params = {};
            if (gender) params.gender = gender;
            if (countryId) params.countryId = countryId;
            
            const response = await axiosInstance.get(`${baseURL}/doctors/zero-login/download`, {
                params,
                responseType: 'blob'
            });
            return response.data;
        } catch (error) {
            return rejectWithValue({ error: getSerializableError(error) });
        }
    }
);

// ✅ Get Doctor Status Counts
export const getDoctorStatusCounts = createAsyncThunk(
    'reports/getDoctorStatusCounts',
    async ({ gender = '', countryId = '' } = {}, { rejectWithValue }) => {
        const axiosInstance = useAxiosAuth();
        try {
            const params = {};
            if (gender) params.gender = gender;
            if (countryId) params.countryId = countryId;
            
            const response = await axiosInstance.get(`${baseURL}/doctors/doctor-Status-Count`, {
                params
            });
            return response.data;
        } catch (error) {
            return rejectWithValue({ error: getSerializableError(error) });
        }
    }
);

// ✅ Get Doctor Patient Count by Date Range
export const doctorPatientCount = createAsyncThunk(
    'reports/doctorPatientCount',
    // `only: 'patients'` — the Super Admin's Patient Count page: the API skips
    // the doctor query and the rows carry doctorCount 0. Omitted, the endpoint
    // behaves exactly as the tenant app's combined chart expects.
    async ({ from, to, gender = '', countryId = '', only = '' }, { rejectWithValue }) => {
        const axiosInstance = useAxiosAuth();
        try {
            const params = { from, to };
            if (gender) params.gender = gender;
            if (countryId) params.countryId = countryId;
            if (only) params.only = only;

            const response = await axiosInstance.get(`${baseURL}/doctors/doctor-patient-count`, {
                params
            });
            return response.data;
        } catch (error) {
            return rejectWithValue({ error: getSerializableError(error) });
        }
    }
);

// ✅ Download Doctor Patient Count Excel
export const downloadDoctorPatientCountExcel = createAsyncThunk(
    'reports/downloadDoctorPatientCountExcel',
    // `only: 'patients'` drops the Doctor Count column from the workbook.
    async ({ from, to, gender = '', countryId = '', only = '' }, { rejectWithValue }) => {
        const axiosInstance = useAxiosAuth();
        try {
            const params = { from, to };
            if (gender) params.gender = gender;
            if (countryId) params.countryId = countryId;
            if (only) params.only = only;

            const response = await axiosInstance.get(`${baseURL}/doctors/doctor-patient-count/download`, {
                params,
                responseType: 'blob'
            });
            return response.data;
        } catch (error) {
            return rejectWithValue({ error: getSerializableError(error) });
        }
    }
);

const reportsSlice = createSlice({
    name: 'reports',
    initialState: {
        doctorsByDate: [],
        patientsByDate: [],
        pendingProfileDoctors: [],
        zeroPatientsDoctors: [],
        zeroAppointmentsDoctors: [],
        lessThanTenAppointmentsDoctors: [],
        zeroLoginDoctors: [],
        doctorStatusCounts: null,
        doctorPatientCountData: [],
        loading: false,
        downloadLoading: false,
        countsLoading: false,
        countDataLoading: false,
        error: null,
        success: false,
        // Pagination for doctors by date
        page: 1,
        totalPages: 0,
        totalItems: 0,
    },
    reducers: {
        clearError: (state) => {
            state.error = null;
        },
        clearReportsData: (state) => {
            state.doctorsByDate = [];
            state.patientsByDate = [];
            state.pendingProfileDoctors = [];
            state.zeroPatientsDoctors = [];
            state.zeroAppointmentsDoctors = [];
            state.lessThanTenAppointmentsDoctors = [];
            state.zeroLoginDoctors = [];
        },
    },
    extraReducers: (builder) => {
        builder
            // Get Doctors by Date
            .addCase(getDoctorsByDate.pending, (state) => {
                state.loading = true;
                state.error = null;
            })
            .addCase(getDoctorsByDate.fulfilled, (state, action) => {
                state.loading = false;
                state.doctorsByDate = action.payload?.data || [];
                state.page = action.payload?.page || 1;
                state.totalPages = action.payload?.totalPages || 0;
                state.totalItems = action.payload?.totalItems || 0;
                state.success = true;
            })
            .addCase(getDoctorsByDate.rejected, (state, action) => {
                state.loading = false;
                state.error = typeof action.payload?.error === 'string' ? action.payload.error : (action.payload?.error?.message || 'Failed to fetch doctors by date');
            })
            // Download Doctors Excel
            .addCase(downloadDoctorsExcel.pending, (state) => {
                state.downloadLoading = true;
                state.error = null;
            })
            .addCase(downloadDoctorsExcel.fulfilled, (state) => {
                state.downloadLoading = false;
                state.success = true;
            })
            .addCase(downloadDoctorsExcel.rejected, (state, action) => {
                state.downloadLoading = false;
                state.error = typeof action.payload?.error === 'string' ? action.payload.error : (action.payload?.error?.message || 'Failed to download doctors excel');
            })
            // Get Patients by Date
            .addCase(getPatientsByDate.pending, (state) => {
                state.loading = true;
                state.error = null;
            })
            .addCase(getPatientsByDate.fulfilled, (state, action) => {
                state.loading = false;
                state.patientsByDate = action.payload?.data || [];
                state.page = action.payload?.page || 1;
                state.totalPages = action.payload?.totalPages || 0;
                state.totalItems = action.payload?.totalItems || 0;
                state.success = true;
            })
            .addCase(getPatientsByDate.rejected, (state, action) => {
                state.loading = false;
                state.error = typeof action.payload?.error === 'string' ? action.payload.error : (action.payload?.error?.message || 'Failed to fetch patients by date');
            })
            // Download Patients Excel
            .addCase(downloadPatientsExcel.pending, (state) => {
                state.downloadLoading = true;
                state.error = null;
            })
            .addCase(downloadPatientsExcel.fulfilled, (state) => {
                state.downloadLoading = false;
                state.success = true;
            })
            .addCase(downloadPatientsExcel.rejected, (state, action) => {
                state.downloadLoading = false;
                state.error = typeof action.payload?.error === 'string' ? action.payload.error : (action.payload?.error?.message || 'Failed to download patients excel');
            })
            // Get Pending Profile Doctors
            .addCase(getPendingProfileDoctors.pending, (state) => {
                state.loading = true;
                state.error = null;
                state.pendingProfileDoctors = { data: [], totalItems: 0, totalPages: 0, page: 1 };
            })
            .addCase(getPendingProfileDoctors.fulfilled, (state, action) => {
                state.loading = false;
                state.pendingProfileDoctors = action.payload;
                state.success = true;
            })
            .addCase(getPendingProfileDoctors.rejected, (state, action) => {
                state.loading = false;
                state.error = typeof action.payload?.error === 'string' ? action.payload.error : (action.payload?.error?.message || 'Failed to fetch pending profile doctors');
            })
            // Download Pending Profile Excel
            .addCase(downloadPendingProfileExcel.pending, (state) => {
                state.downloadLoading = true;
                state.error = null;
            })
            .addCase(downloadPendingProfileExcel.fulfilled, (state) => {
                state.downloadLoading = false;
                state.success = true;
            })
            .addCase(downloadPendingProfileExcel.rejected, (state, action) => {
                state.downloadLoading = false;
                state.error = typeof action.payload?.error === 'string' ? action.payload.error : (action.payload?.error?.message || 'Failed to download pending profile excel');
            })
            // Get Zero Patients Doctors
            .addCase(getZeroPatientsDoctors.pending, (state) => {
                state.loading = true;
                state.error = null;
                state.zeroPatientsDoctors = { data: [], totalItems: 0, totalPages: 0, page: 1 };
            })
            .addCase(getZeroPatientsDoctors.fulfilled, (state, action) => {
                state.loading = false;
                state.zeroPatientsDoctors = action.payload;
                state.success = true;
            })
            .addCase(getZeroPatientsDoctors.rejected, (state, action) => {
                state.loading = false;
                state.error = typeof action.payload?.error === 'string' ? action.payload.error : (action.payload?.error?.message || 'Failed to fetch zero patients doctors');
            })
            // Download Zero Patients Excel
            .addCase(downloadZeroPatientsExcel.pending, (state) => {
                state.downloadLoading = true;
                state.error = null;
            })
            .addCase(downloadZeroPatientsExcel.fulfilled, (state) => {
                state.downloadLoading = false;
                state.success = true;
            })
            .addCase(downloadZeroPatientsExcel.rejected, (state, action) => {
                state.downloadLoading = false;
                state.error = typeof action.payload?.error === 'string' ? action.payload.error : (action.payload?.error?.message || 'Failed to download zero patients excel');
            })
            // Get Zero Appointments Doctors
            .addCase(getZeroAppointmentsDoctors.pending, (state) => {
                state.loading = true;
                state.error = null;
                state.zeroAppointmentsDoctors = { data: [], totalItems: 0, totalPages: 0, page: 1 };
            })
            .addCase(getZeroAppointmentsDoctors.fulfilled, (state, action) => {
                state.loading = false;
                state.zeroAppointmentsDoctors = action.payload;
                state.success = true;
            })
            .addCase(getZeroAppointmentsDoctors.rejected, (state, action) => {
                state.loading = false;
                state.error = typeof action.payload?.error === 'string' ? action.payload.error : (action.payload?.error?.message || 'Failed to fetch zero appointments doctors');
            })
            // Download Zero Appointments Excel
            .addCase(downloadZeroAppointmentsExcel.pending, (state) => {
                state.downloadLoading = true;
                state.error = null;
            })
            .addCase(downloadZeroAppointmentsExcel.fulfilled, (state) => {
                state.downloadLoading = false;
                state.success = true;
            })
            .addCase(downloadZeroAppointmentsExcel.rejected, (state, action) => {
                state.downloadLoading = false;
                state.error = typeof action.payload?.error === 'string' ? action.payload.error : (action.payload?.error?.message || 'Failed to download zero appointments excel');
            })
            // Get Less Than Ten Appointments Doctors
            .addCase(getLessThanTenAppointmentsDoctors.pending, (state) => {
                state.loading = true;
                state.error = null;
                state.lessThanTenAppointmentsDoctors = { data: [], totalItems: 0, totalPages: 0, page: 1 };
            })
            .addCase(getLessThanTenAppointmentsDoctors.fulfilled, (state, action) => {
                state.loading = false;
                state.lessThanTenAppointmentsDoctors = action.payload;
                state.success = true;
            })
            .addCase(getLessThanTenAppointmentsDoctors.rejected, (state, action) => {
                state.loading = false;
                state.error = typeof action.payload?.error === 'string' ? action.payload.error : (action.payload?.error?.message || 'Failed to fetch less than ten appointments doctors');
            })
            // Download Less Than Ten Appointments Excel
            .addCase(downloadLessThanTenAppointmentsExcel.pending, (state) => {
                state.downloadLoading = true;
                state.error = null;
            })
            .addCase(downloadLessThanTenAppointmentsExcel.fulfilled, (state) => {
                state.downloadLoading = false;
                state.success = true;
            })
            .addCase(downloadLessThanTenAppointmentsExcel.rejected, (state, action) => {
                state.downloadLoading = false;
                state.error = typeof action.payload?.error === 'string' ? action.payload.error : (action.payload?.error?.message || 'Failed to download less than ten appointments excel');
            })
            // Get Zero Login Doctors
            .addCase(getZeroLoginDoctors.pending, (state) => {
                state.loading = true;
                state.error = null;
                state.zeroLoginDoctors = { data: [], totalItems: 0, totalPages: 0, page: 1 };
            })
            .addCase(getZeroLoginDoctors.fulfilled, (state, action) => {
                state.loading = false;
                state.zeroLoginDoctors = action.payload;
                state.success = true;
            })
            .addCase(getZeroLoginDoctors.rejected, (state, action) => {
                state.loading = false;
                state.error = typeof action.payload?.error === 'string' ? action.payload.error : (action.payload?.error?.message || 'Failed to fetch zero login doctors');
            })
            // Download Zero Login Excel
            .addCase(downloadZeroLoginExcel.pending, (state) => {
                state.downloadLoading = true;
                state.error = null;
            })
            .addCase(downloadZeroLoginExcel.fulfilled, (state) => {
                state.downloadLoading = false;
                state.success = true;
            })
            .addCase(downloadZeroLoginExcel.rejected, (state, action) => {
                state.downloadLoading = false;
                state.error = typeof action.payload?.error === 'string' ? action.payload.error : (action.payload?.error?.message || 'Failed to download zero login excel');
            })
            // Get Doctor Status Counts
            .addCase(getDoctorStatusCounts.pending, (state) => {
                state.countsLoading = true;
                state.error = null;
            })
            .addCase(getDoctorStatusCounts.fulfilled, (state, action) => {
                state.countsLoading = false;
                state.doctorStatusCounts = action.payload;
                state.success = true;
            })
            .addCase(getDoctorStatusCounts.rejected, (state, action) => {
                state.countsLoading = false;
                state.error = typeof action.payload?.error === 'string' ? action.payload.error : (action.payload?.error?.message || 'Failed to fetch doctor status counts');
            })
            // Get Doctor Patient Count
            .addCase(doctorPatientCount.pending, (state) => {
                state.countDataLoading = true;
                state.error = null;
            })
            .addCase(doctorPatientCount.fulfilled, (state, action) => {
                state.countDataLoading = false;
                state.doctorPatientCountData = action.payload?.data || [];
                state.success = true;
            })
            .addCase(doctorPatientCount.rejected, (state, action) => {
                state.countDataLoading = false;
                state.error = typeof action.payload?.error === 'string' ? action.payload.error : (action.payload?.error?.message || 'Failed to fetch doctor patient count');
            })
            // Download Doctor Patient Count Excel
            .addCase(downloadDoctorPatientCountExcel.pending, (state) => {
                state.downloadLoading = true;
                state.error = null;
            })
            .addCase(downloadDoctorPatientCountExcel.fulfilled, (state) => {
                state.downloadLoading = false;
                state.success = true;
            })
            .addCase(downloadDoctorPatientCountExcel.rejected, (state, action) => {
                state.downloadLoading = false;
                state.error = typeof action.payload?.error === 'string' ? action.payload.error : (action.payload?.error?.message || 'Failed to download doctor patient count excel');
            });
    },
});

export const { clearError, clearReportsData } = reportsSlice.actions;
export default reportsSlice.reducer;

